import { newClientId } from "./client-id.js";

/**
 * clientNonce prefix for messages a client sends from a live voice call. The
 * nonce is the only channel that carries the call's identity: the server reads
 * it to answer in spoken sentences, and clients group one call's exchange into
 * a single transcript card.
 */
export const CALL_CLIENT_NONCE_PREFIX = "call:";

export function callClientNonce(callId: string): string {
  if (callId.includes(":")) throw new Error(`callId must not contain ":": ${callId}`);
  return `${CALL_CLIENT_NONCE_PREFIX}${callId}:${newClientId()}`;
}

/** A deterministic id for one ASR sentence submitted from a live call. */
export function callTurnClientNonce(callId: string, sentenceId: number): string {
  if (callId.includes(":") || !Number.isSafeInteger(sentenceId) || sentenceId < 1)
    throw new Error("Invalid call turn");
  return `${CALL_CLIENT_NONCE_PREFIX}${callId}:turn:${sentenceId}`;
}

export function isCallClientNonce(clientNonce: string | null | undefined): boolean {
  return Boolean(clientNonce?.startsWith(CALL_CLIENT_NONCE_PREFIX));
}

export function callIdFromClientNonce(clientNonce: string | null | undefined): string | undefined {
  if (!clientNonce || !isCallClientNonce(clientNonce)) return undefined;
  const turn = callTurnFromClientNonce(clientNonce);
  if (turn) return turn.callId;
  const rest = clientNonce.slice(CALL_CLIENT_NONCE_PREFIX.length);
  const suffixAt = rest.lastIndexOf(":");
  return suffixAt === -1 ? undefined : rest.slice(0, suffixAt) || undefined;
}

export function callTurnFromClientNonce(
  clientNonce: string | null | undefined,
): { callId: string; sentenceId: number } | undefined {
  if (!clientNonce?.startsWith(CALL_CLIENT_NONCE_PREFIX)) return undefined;
  const match = /^call:([^:]+):turn:([1-9]\d*)$/.exec(clientNonce);
  if (!match) return undefined;
  const sentenceId = Number(match[2]);
  return Number.isSafeInteger(sentenceId) ? { callId: match[1]!, sentenceId } : undefined;
}
